import { createSelector } from '@reduxjs/toolkit';
import { ReduxModel } from 'core/models';
import { IHierarchy } from 'core/models/report-builder/report-builder';
import { IExpressionStoreLike } from 'core/utils/report-expressions';

const reportSelector = ({ reportStoreV2 }: ReduxModel.IGlobalState) => reportStoreV2;
const reportRequestProcessingSelector = ({ reportStoreV2: { requestProcessing } }: ReduxModel.IGlobalState) => requestProcessing;
const reportIdSelector = ({ reportStoreV2: { reportId } }: ReduxModel.IGlobalState) => reportId;
const reportConfigSelector = ({ reportStoreV2: { reportConfig } }: ReduxModel.IGlobalState) => reportConfig;
const reportActiveFilterSelector = ({ reportStoreV2: { activeFilters } }: ReduxModel.IGlobalState) => activeFilters;
const reportSortSelector = ({ reportStoreV2: { sort } }: ReduxModel.IGlobalState) => sort;
const reportPageSelector = ({ reportStoreV2: { page } }: ReduxModel.IGlobalState) => page;
const expressionFieldsSelector = ({ reportStoreV2: { fields } }: ReduxModel.IGlobalState) => fields;
const savedReportConfigSelector = ({ reportStoreV2: { savedReportConfig } }: ReduxModel.IGlobalState) => savedReportConfig;
const columnsSelector = ({ reportStoreV2: { columns } }: ReduxModel.IGlobalState) => columns;
const appliedDimensionsSelector = ({ reportStoreV2: { appliedDimensions } }: ReduxModel.IGlobalState) => appliedDimensions;
const allMeasuresSelector = ({ reportStoreV2: { allMeasures } }: ReduxModel.IGlobalState) => allMeasures;
const chartAttributeSelector = ({ reportStoreV2: { selectedChartData: { attributeKey } } }: ReduxModel.IGlobalState) => attributeKey;
const chartSelector = ({ reportStoreV2: { selectedChartData } }: ReduxModel.IGlobalState) => selectedChartData;
const metaFieldOptionsSelector = ({ reportStoreV2: { entityGroupOptionsMap } }: ReduxModel.IGlobalState) => entityGroupOptionsMap;
const reportErrorSelector = ({ reportStoreV2: { error } }: ReduxModel.IGlobalState) => error;
const conditionalFormattingSelectorForReportPage = ({ reportStoreV2: { ConditionalFormatting } }: ReduxModel.IGlobalState) => ConditionalFormatting;
const conditionalFomattingSelectorForGridRendering = ({ reportStoreV2: { reportConfig } }: ReduxModel.IGlobalState) => reportConfig?.Visualization?.ConditionalFormatting;
// --- Broken custom-filter-variable detection on the report (view) page ---
// Mirrors the builder's brokenCustomFilterExpressionsSelector, but sources from the saved report
// config: expressions live inline on each column (Props.Measure/Dimension.Expression) rather than in
// a separate expressionStore, and definitions live on reportConfig.CustomFilterDefinitions.

// Adapt the report's custom columns/values into the expression-store-like shape the detection core
// expects: keyed by Column.Id, carrying the column's display name and its jsonExpression. Column
// Expression refs are stable across renders, so the core util's per-tree WeakMap cache still hits.
const reportCustomExpressionStoreSelector = createSelector(
  reportConfigSelector,
  (reportConfig): IExpressionStoreLike => {
    const store: IExpressionStoreLike = {};
    (reportConfig?.Columns || []).forEach((column) => {
      const measureExpression = (column?.Props?.Measure?.IsCustomExpression && column?.Props?.Measure?.Expression);
      const dimensionExpression = (column?.Props?.Dimension?.IsCustomExpression && column?.Props?.Dimension?.Expression);
      const expression = measureExpression || dimensionExpression;
      if (expression) store[String(column.Id)] = { displayName: column.Name, jsonExpression: expression };
    });
    return store;
  },
);

export {
  reportSelector,
  reportIdSelector,
  reportRequestProcessingSelector,
  reportConfigSelector,
  reportActiveFilterSelector,
  reportSortSelector,
  reportPageSelector,
  expressionFieldsSelector,
  savedReportConfigSelector,
  columnsSelector,
  appliedDimensionsSelector,
  allMeasuresSelector,
  chartAttributeSelector,
  chartSelector,
  metaFieldOptionsSelector,
  reportErrorSelector,
  conditionalFormattingSelectorForReportPage,
  conditionalFomattingSelectorForGridRendering,
  reportCustomExpressionStoreSelector,
};
